import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import { isVesselAuthenticated } from "./lib/vesselAuth";
import { isDeptAuthenticated } from "./lib/deptAuth";
import Layout from "./components/Layout";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import TransfersPage from "./pages/TransfersPage";
import VesselsPage from "./pages/VesselsPage";
import MessagesPage from "./pages/MessagesPage";
import AuditPage from "./pages/AuditPage";
import DownloadPage from "./pages/DownloadPage";
import VesselPortalPage from "./pages/VesselPortalPage";
import VesselLogin from "./pages/VesselLogin";
import VesselPortal from "./pages/VesselPortal";
import ContactsPage from "./pages/ContactsPage";
import SettingsPage from "./pages/SettingsPage";
import RequestAccessPage from "./pages/RequestAccessPage";
import DeptLogin from "./pages/DeptLogin";
import DeptPortal from "./pages/DeptPortal";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { token } = useAuth();
  return token ? <>{children}</> : <Navigate to="/login" replace />;
}

function RequireVesselAuth({ children }: { children: React.ReactNode }) {
  return isVesselAuthenticated() ? <>{children}</> : <Navigate to="/vessel/login" replace />;
}

function RequireDeptAuth({ children }: { children: React.ReactNode }) {
  return isDeptAuthenticated() ? <>{children}</> : <Navigate to="/dept/login" replace />;
}

function AuthLayout({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <Layout>{children}</Layout>
    </RequireAuth>
  );
}

export default function App() {
  return (
    <Routes>
      {/* Shore auth */}
      <Route path="/login" element={<LoginPage />} />

      {/* Vessel auth — static segments match before /:vesselId */}
      <Route path="/vessel/login" element={<VesselLogin />} />
      <Route path="/vessel/portal" element={<RequireVesselAuth><VesselPortal /></RequireVesselAuth>} />

      {/* Department portal */}
      <Route path="/dept/login" element={<DeptLogin />} />
      <Route path="/dept/portal" element={<RequireDeptAuth><DeptPortal /></RequireDeptAuth>} />

      {/* Unauthenticated public pages */}
      <Route path="/download/:token" element={<DownloadPage />} />
      <Route path="/vessel/:vesselId" element={<VesselPortalPage />} />

      {/* Public landing page */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/request-access" element={<RequestAccessPage />} />

      {/* Shore authenticated — wrapped in Layout sidebar */}
      <Route path="/dashboard" element={<AuthLayout><DashboardPage /></AuthLayout>} />
      <Route path="/transfers" element={<AuthLayout><TransfersPage /></AuthLayout>} />
      <Route path="/vessels"   element={<AuthLayout><VesselsPage /></AuthLayout>} />
      <Route path="/messages"  element={<AuthLayout><MessagesPage /></AuthLayout>} />
      <Route path="/contacts"  element={<AuthLayout><ContactsPage /></AuthLayout>} />
      <Route path="/audit"     element={<AuthLayout><AuditPage /></AuthLayout>} />
      <Route path="/settings"  element={<AuthLayout><SettingsPage /></AuthLayout>} />

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
